/**
 * Billing Tenant Detail Page (Super Admin)
 * Info, subscriptions, historico, ajuste manual
 */

import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card, CardContent, CardHeader, CardTitle, Button, Input,
  useToast, Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@payflow/ui";

const API_BASE = (import.meta as any).env?.VITE_API_URL || "http://localhost:8787";
const TOKEN_KEY = "payflow_token";

interface TenantDetail {
  tenant: {
    id: string;
    nome: string;
    documento: string;
    email: string;
    plano: string;
    status: string;
    subscription_amount: string | null;
    next_billing_date: string | null;
    billing_email: string | null;
    billing_phone: string | null;
    created_at: string;
  };
  subscriptions: Array<{
    id: string;
    periodStart: string;
    periodEnd: string;
    plan: string;
    amount: string;
    status: string;
    dueDate: string;
    paidAt: string | null;
  }>;
  payments: Array<{
    id: string;
    amount: string;
    method: string;
    paidAt: string;
    referenceMonth: string;
    notes: string | null;
  }>;
}

export function BillingTenantDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [data, setData] = useState<TenantDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [showAdjust, setShowAdjust] = useState(false);
  const [saving, setSaving] = useState(false);
  const [adjustForm, setAdjustForm] = useState({
    amount: "",
    referenceMonth: "",
    notes: "",
  });

  const fetchData = () => {
    const token = localStorage.getItem(TOKEN_KEY);
    setLoading(true);
    fetch(`${API_BASE}/v1/admin/billing/tenants/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((res) => {
        if (res.success) setData(res.data);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  const handleAdjust = async () => {
    if (!adjustForm.amount || !adjustForm.referenceMonth) {
      toast({ variant: "warning", title: "Preencha valor e mes de referencia" });
      return;
    }
    setSaving(true);
    const token = localStorage.getItem(TOKEN_KEY);
    try {
      const res = await fetch(`${API_BASE}/v1/admin/billing/tenants/${id}/adjust`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          amount: Number(adjustForm.amount),
          referenceMonth: adjustForm.referenceMonth,
          notes: adjustForm.notes,
        }),
      });
      const result = await res.json();
      if (result.success) {
        toast({ variant: "success", title: "Pagamento registrado com sucesso" });
        setShowAdjust(false);
        setAdjustForm({ amount: "", referenceMonth: "", notes: "" });
        fetchData();
      } else {
        toast({ variant: "error", title: result.error?.message || "Erro ao registrar pagamento" });
      }
    } catch {
      toast({ variant: "error", title: "Erro de conexao" });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="py-12 text-center text-muted-foreground">Carregando...</div>;
  if (!data) return <div className="py-12 text-center text-red-500">Tenant nao encontrado</div>;

  const { tenant, subscriptions, payments } = data;

  const statusColor = (s: string) => {
    switch (s) {
      case "active": case "paid": return "bg-green-100 text-green-700";
      case "suspended": case "overdue": return "bg-red-100 text-red-700";
      case "pending": return "bg-yellow-100 text-yellow-700";
      default: return "bg-gray-100 text-gray-700";
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <Button variant="ghost" size="sm" onClick={() => navigate("/gestor/tenants")}>
            &larr; Voltar
          </Button>
          <h1 className="mt-1 text-2xl font-bold text-foreground">{tenant.nome}</h1>
        </div>
        <Button onClick={() => setShowAdjust(true)}>
          Registrar pagamento
        </Button>
      </div>

      {/* Tenant Info */}
      <Card>
        <CardHeader><CardTitle>Informacoes</CardTitle></CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3 text-sm">
            <div>
              <span className="text-muted-foreground">CNPJ:</span>
              <p className="font-medium text-foreground">{tenant.documento}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Email:</span>
              <p className="font-medium text-foreground">{tenant.email}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Plano:</span>
              <p className="font-medium text-foreground">{tenant.plano}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Status:</span>
              <p><span className={`rounded px-2 py-0.5 text-xs font-medium ${statusColor(tenant.status)}`}>{tenant.status}</span></p>
            </div>
            <div>
              <span className="text-muted-foreground">Valor mensal:</span>
              <p className="font-medium text-foreground">{tenant.subscription_amount ? `R$ ${tenant.subscription_amount}` : "-"}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Proximo vencimento:</span>
              <p className="font-medium text-foreground">{tenant.next_billing_date ? new Date(tenant.next_billing_date).toLocaleDateString("pt-BR") : "-"}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Adjust Dialog */}
      <Dialog
        open={showAdjust}
        onOpenChange={setShowAdjust}
        dirty={
          adjustForm.amount !== "" ||
          adjustForm.referenceMonth !== "" ||
          adjustForm.notes !== ""
        }
        onSave={async () => { await handleAdjust(); }}
      >
        <DialogContent size="md">
          <DialogHeader>
            <DialogTitle>Registrar pagamento manual</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Input
              label="Valor (R$)"
              type="number"
              value={adjustForm.amount}
              onChange={(e) => setAdjustForm({ ...adjustForm, amount: e.target.value })}
              placeholder="199.90"
            />
            <Input
              label="Mes referencia (YYYY-MM)"
              value={adjustForm.referenceMonth}
              onChange={(e) => setAdjustForm({ ...adjustForm, referenceMonth: e.target.value })}
              placeholder="2026-02"
            />
            <Input
              label="Observacao"
              value={adjustForm.notes}
              onChange={(e) => setAdjustForm({ ...adjustForm, notes: e.target.value })}
              placeholder="Pagamento via transferencia"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdjust(false)}>
              Cancelar
            </Button>
            <Button loading={saving} onClick={handleAdjust}>
              Registrar pagamento
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Subscriptions */}
      <Card>
        <CardHeader><CardTitle>Subscriptions</CardTitle></CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Periodo</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Plano</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Valor</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Status</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Vencimento</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Pago em</th>
              </tr>
            </thead>
            <tbody>
              {subscriptions.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-4 text-center text-muted-foreground">Sem subscriptions</td></tr>
              ) : (
                subscriptions.map((s) => (
                  <tr key={s.id} className="border-b border-border">
                    <td className="px-4 py-3 text-foreground">{s.periodStart} ~ {s.periodEnd}</td>
                    <td className="px-4 py-3 text-foreground">{s.plan}</td>
                    <td className="px-4 py-3 text-foreground">R$ {s.amount}</td>
                    <td className="px-4 py-3">
                      <span className={`rounded px-2 py-0.5 text-xs font-medium ${statusColor(s.status)}`}>{s.status}</span>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{s.dueDate}</td>
                    <td className="px-4 py-3 text-muted-foreground">{s.paidAt ? new Date(s.paidAt).toLocaleDateString("pt-BR") : "-"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Payment History */}
      <Card>
        <CardHeader><CardTitle>Historico de pagamentos</CardTitle></CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Data</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Valor</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Metodo</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Ref.</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Obs.</th>
              </tr>
            </thead>
            <tbody>
              {payments.length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-4 text-center text-muted-foreground">Sem pagamentos</td></tr>
              ) : (
                payments.map((p) => (
                  <tr key={p.id} className="border-b border-border">
                    <td className="px-4 py-3 text-foreground">{new Date(p.paidAt).toLocaleDateString("pt-BR")}</td>
                    <td className="px-4 py-3 text-foreground">R$ {p.amount}</td>
                    <td className="px-4 py-3">
                      <span className={`rounded px-2 py-0.5 text-xs font-medium ${p.method === "pix" ? "bg-green-100 text-green-700" : "bg-blue-100 text-blue-700"}`}>
                        {p.method}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{p.referenceMonth}</td>
                    <td className="px-4 py-3 text-muted-foreground">{p.notes || "-"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
